// frontend/src/components/calendar/CalendarView.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs';
import {
    Box,
    Paper,
    Skeleton,
    Container,
    Snackbar,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    alpha,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import RefreshIcon from '@mui/icons-material/Refresh';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { scheduledPostsApi } from '../../services/api';
import { trackCalendarSlotClicked, trackScheduleDrawerOpened } from '../../services/analytics';
import CalendarToolbar from './CalendarToolbar';
import CalendarEvent from './CalendarEvent';
import PostDetailDrawer from './PostDetailDrawer';
import SchedulePostDrawer from './SchedulePostDrawer';
import UpcomingSidebar from './UpcomingSidebar';


const localizer = dayjsLocalizer(dayjs);

const CalendarView = ({ onPost }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [view, setView] = useState('month');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [scheduleDrawerDate, setScheduleDrawerDate] = useState(null);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [pickedDate, setPickedDate] = useState(null);
    const [activePlatforms, setActivePlatforms] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchEvents = useCallback(async (start, end) => {
        setLoading(true);
        setError(null);
        try {
            const startIso = dayjs(start).startOf('day').toISOString();
            const endIso = dayjs(end).endOf('day').toISOString();
            const data = await scheduledPostsApi.getRange(startIso, endIso);

            const normalizedEvents = (data || []).map(post => ({
                id: post.id,
                title: (post.platforms && post.platforms[0]) || 'Scheduled Post',
                start: new Date(post.scheduledFor),
                end: new Date(post.scheduledFor),
                resource: post
            }));
            setEvents(normalizedEvents);
        } catch (err) {
            setError(err.message || 'Failed to load scheduled posts. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const start = dayjs(currentDate).startOf(view === 'month' ? 'month' : 'week').subtract(1, 'week').toDate();
        const end = dayjs(currentDate).endOf(view === 'month' ? 'month' : 'week').add(1, 'week').toDate();
        fetchEvents(start, end);
    }, [currentDate, view, fetchEvents, retryCount]);

    const handleRetry = () => {
        setRetryCount(prev => prev + 1);
    };

    const filteredEvents = useMemo(() => {
        if (activePlatforms.length === 0) return events;
        return events.filter(event =>
            event.resource.platforms.some(p => activePlatforms.includes(p))
        );
    }, [events, activePlatforms]);

    const handlePlatformToggle = (platform) => {
        if (platform === null) {
            setActivePlatforms([]);
        } else {
            setActivePlatforms(prev =>
                prev.includes(platform)
                    ? prev.filter(p => p !== platform)
                    : [...prev, platform]
            );
        }
    };

    const handleScheduleNewClick = () => {
        setPickedDate(dayjs().add(1, 'hour').startOf('hour'));
        setDatePickerOpen(true);
        trackScheduleDrawerOpened('sidebar_button');
    };

    const handleDatePickerConfirm = () => {
        if (!pickedDate) return;
        setDatePickerOpen(false);
        setScheduleDrawerDate(pickedDate.toISOString());
        setScheduleDrawerOpen(true);
    };

    const handleSlotSelect = ({ start }) => {
        const slotDay = dayjs(start);
        // In month view, react-big-calendar passes midnight (00:00) for the clicked day.
        // Compare at the "day" level so clicking today is allowed (user picks time inside the drawer).
        // For week/day views the time is exact, so compare at minute level.
        const isPastDay = slotDay.isBefore(dayjs(), 'day');
        if (isPastDay) {
            setSnackbar({ open: true, message: 'Cannot schedule a post in the past.', severity: 'warning' });
            return;
        }
        trackCalendarSlotClicked();
        trackScheduleDrawerOpened('slot');
        setScheduleDrawerDate(start.toISOString());
        setScheduleDrawerOpen(true);
    };

    const handleEventSelect = (event) => {
        setSelectedEvent(event.resource);
        setDrawerOpen(true);
    };

    const handleCancelled = (postId) => {
        setEvents(prev => prev.filter(e => e.id !== postId));
        setDrawerOpen(false);
        setSnackbar({ open: true, message: 'Post cancelled successfully', severity: 'success' });
    };

    const renderCalendarBody = () => {
        // Initial loading — no events yet
        if (loading && events.length === 0) {
            return (
                <Box sx={{ p: 2 }}>
                    <Skeleton variant="rectangular" height={600} sx={{ borderRadius: '6px' }} />
                </Box>
            );
        }

        // First load failed with no cached events — show full error state
        if (error && events.length === 0) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 500,
                        gap: 2,
                        p: 4,
                    }}
                >
                    <Alert
                        severity="error"
                        sx={{ width: '100%', maxWidth: 520, borderRadius: '6px' }}
                    >
                        {error}
                    </Alert>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRetry}
                        sx={{ borderRadius: '4px' }}
                    >
                        Try Again
                    </Button>
                </Box>
            );
        }

        return (
            <>
                {/* Inline banner when a navigation refresh fails but cached events are visible */}
                {error && (
                    <Alert
                        severity="error"
                        onClose={() => setError(null)}
                        action={
                            <Button color="inherit" size="small" onClick={handleRetry}>
                                Retry
                            </Button>
                        }
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        {error}
                    </Alert>
                )}
                <Calendar
                    localizer={localizer}
                    events={filteredEvents}
                    view={view}
                    date={currentDate}
                    onNavigate={setCurrentDate}
                    onView={setView}
                    onSelectSlot={handleSlotSelect}
                    onSelectEvent={handleEventSelect}
                    selectable
                    components={{
                        toolbar: (props) => (
                            <CalendarToolbar
                                {...props}
                                activePlatforms={activePlatforms}
                                onPlatformToggle={handlePlatformToggle}
                            />
                        ),
                        event: CalendarEvent,
                    }}
                    style={{ height: 'calc(100vh - 220px)', minHeight: 480 }}
                />
            </>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <Paper sx={{
                        p: { xs: 1.5, sm: 2, md: 3 }, borderRadius: '6px',
                        bgcolor: 'background.paper', overflow: 'hidden',
                        // ── react-big-calendar dark mode overrides ──────────
                        ...(isDark && {
                            '& .rbc-calendar, & .rbc-month-view, & .rbc-time-view, & .rbc-agenda-view': {
                                bgcolor: 'transparent',
                                color: theme.palette.text.primary,
                            },
                            '& .rbc-header': {
                                bgcolor: alpha(theme.palette.background.default, 0.5),
                                borderColor: alpha(theme.palette.divider, 0.2),
                                color: theme.palette.text.secondary,
                            },
                            '& .rbc-month-row, & .rbc-day-bg, & .rbc-time-slot, & .rbc-timeslot-group, & .rbc-time-content, & .rbc-time-header-content': {
                                borderColor: `${alpha(theme.palette.divider, 0.15)} !important`,
                            },
                            '& .rbc-today': {
                                bgcolor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
                            },
                            '& .rbc-off-range-bg': {
                                bgcolor: `${alpha(theme.palette.background.default, 0.6)} !important`,
                            },
                            '& .rbc-off-range': {
                                color: `${theme.palette.text.disabled} !important`,
                            },
                            '& .rbc-date-cell': {
                                color: theme.palette.text.secondary,
                            },
                            '& .rbc-show-more': {
                                color: `${theme.palette.primary.main} !important`,
                                bgcolor: 'transparent',
                            },
                            '& .rbc-current-time-indicator': {
                                bgcolor: theme.palette.primary.main,
                            },
                            '& .rbc-time-header.rbc-overflowing': {
                                borderColor: alpha(theme.palette.divider, 0.15),
                            },
                            '& .rbc-allday-cell': {
                                borderColor: alpha(theme.palette.divider, 0.15),
                            },
                            '& .rbc-time-gutter .rbc-timeslot-group': {
                                bgcolor: 'transparent',
                            },
                            '& .rbc-label': {
                                color: theme.palette.text.secondary,
                            },
                        }),
                    }}>
                        {renderCalendarBody()}
                    </Paper>
                </Box>

                <Box sx={{ width: { xs: '100%', lg: 280 }, flexShrink: 0 }}>
                    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '6px', height: 'fit-content' }}>
                        <UpcomingSidebar
                            events={events}
                            onEventClick={(resource) => {
                                setSelectedEvent(resource);
                                setDrawerOpen(true);
                            }}
                            onScheduleNew={handleScheduleNewClick}
                        />
                    </Paper>
                </Box>
            </Box>

            <PostDetailDrawer
                open={drawerOpen}
                post={selectedEvent}
                onClose={() => setDrawerOpen(false)}
                onCancelled={handleCancelled}
            />

            <SchedulePostDrawer
                open={scheduleDrawerOpen}
                onClose={() => setScheduleDrawerOpen(false)}
                onPost={onPost}
                initialDate={scheduleDrawerDate}
                initialPlatforms={activePlatforms.length > 0 ? activePlatforms : null}
            />

            {/* Date picker dialog for "Schedule New Post" button */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Dialog
                    open={datePickerOpen}
                    onClose={() => setDatePickerOpen(false)}
                    PaperProps={{ sx: { borderRadius: '8px', p: 1 } }}
                >
                    <DialogTitle sx={{ fontWeight: 700 }}>Pick a date & time</DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <DateTimePicker
                            label="Schedule for"
                            value={pickedDate}
                            onChange={setPickedDate}
                            minDateTime={dayjs().add(5, 'minute')}
                            slotProps={{
                                textField: { fullWidth: true, sx: { mt: 1 } },
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setDatePickerOpen(false)} sx={{ textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleDatePickerConfirm}
                            disabled={!pickedDate || pickedDate.isBefore(dayjs())}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: '6px',
                                background: 'linear-gradient(45deg, #6366f1, #818cf8)',
                            }}
                        >
                            Continue
                        </Button>
                    </DialogActions>
                </Dialog>
            </LocalizationProvider>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CalendarView;
